import {
    CheckoutChargeStatusType,
    DataTablePresenter,
    Invoice,
    InvoiceDetail,
    InvoiceType,
    ITableColumn,
    ProviderClaimPayment,
    ProviderGuarantor,
    ProviderPaymentPlan,
    renderAmount,
    renderDate,
    SupportCheckoutCharge,
    SupportCreditCard,
    TimelineSegment,
    Transaction,
} from '@enter/core';
import { mdiChevronDown, mdiChevronRight } from '@mdi/js';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Row } from 'react-table';
import { DialogContext, DialogProvider } from 'services';
import { renderEnum } from 'utils';
import * as S from './billing-timeline-table.styles';
import { CheckoutChargeDetail } from './checkout-charge-detail';
import { GuarantorDetail } from './guarantor-detail';
import { RefundDetail } from './refund-detail';
import { TransactionDetail } from './transaction-detail';

interface IBillingTimelineTableProps {
    loading: boolean;
    checkoutCharges: SupportCheckoutCharge[] | undefined;
    claimId: string;
    creditCards: SupportCreditCard[] | undefined;
    guarantor?: ProviderGuarantor;
    invoices?: Invoice[];
    paymentPlans: ProviderPaymentPlan[] | undefined;
    transactions?: Transaction[];
    payments?: ProviderClaimPayment[];
    Header?: React.ReactNode;
}

interface IBillingTimelineEvent {
    id?: string;
    created?: string;
    event?: string;
    open?: boolean;
    description?: React.ReactElement;
    Component?: React.ReactElement | (() => React.ReactElement);
    selectable?: boolean;
    groupedItems?: IBillingTimelineEvent[];
    isExpanded?: boolean;
}

export const BillingTimelineTable: React.FC<IBillingTimelineTableProps> = (props: IBillingTimelineTableProps) => {
    const {
        loading,
        checkoutCharges,
        claimId,
        creditCards,
        guarantor,
        invoices,
        paymentPlans,
        transactions,
        payments,
        Header,
    } = props;

    const dialogContext = useContext(DialogContext);
    const [expandedIds, setExpandedIds] = useState<string[]>([]);

    const columns: ITableColumn<IBillingTimelineEvent>[] = [
        {
            id: 'link',
            maxWidth: 30,
            Cell: ({ cell }): React.ReactElement => {
                return (
                    <S.StyledSegment selectable={!!cell.row.original.Component}>
                        <TimelineSegment start={cell.row.index === 0} />
                    </S.StyledSegment>
                );
            },
        },
        {
            id: 'dateCreated',
            Header: 'Date Created',
            accessor: 'created',
            maxWidth: 200,
            Cell: ({ cell }): React.ReactElement => (
                <S.SelectableCell selectable={!!cell.row.original.Component}>{renderDate(cell.value)}</S.SelectableCell>
            ),
        },
        {
            id: 'event',
            Header: 'Event',
            accessor: 'event',
            maxWidth: 200,
            Cell: ({ cell }): React.ReactElement => (
                <S.SelectableCell selectable={!!cell.row.original.Component}>
                    <S.StyledEvent>{cell.value}</S.StyledEvent>
                </S.SelectableCell>
            ),
        },
        {
            id: 'description',
            Header: 'Description',
            accessor: 'description',
            maxWidth: 10000, // NB: only way to emulate "flex-grow: 1" expansion
            Cell: ({ cell }): React.ReactElement => {
                return (
                    <S.StyledDescription selectable={!!cell.row.original.Component}>
                        <S.Description>{cell.value}</S.Description>
                    </S.StyledDescription>
                );
            },
        },
        {
            id: 'selectable',
            maxWidth: 40,
            Cell: ({ cell }): React.ReactElement | null => {
                if (!!cell.row.original.Component) {
                    const path = cell.row.isExpanded ? mdiChevronDown : mdiChevronRight;
                    return (
                        <S.SelectableCell selectable={!!cell.row.original.Component}>
                            <S.StyledIcon path={path} />
                        </S.SelectableCell>
                    );
                }
                return null;
            },
        },
    ];
    const presenter = new DataTablePresenter({
        name: 'billing-timeline',
        data: [],
        columns,
        filterSchemas: [],
    });
    const tableDefaults = presenter.getTableDefaults();
    presenter.saveTableDefaults({
        ...tableDefaults,
        pageSize: 100,
    });

    const compileEvents = useCallback(() => {
        const guarantorEvent = (): IBillingTimelineEvent[] => {
            const result = [];
            if (guarantor) {
                const parts = [
                    `${guarantor.first} ${guarantor.last}`,
                    guarantor.phone,
                    guarantor.email,
                    `Payment Plan: ${_.isEmpty(paymentPlans) ? 'No' : 'Yes'}`,
                ];
                result.push({
                    id: guarantor.id,
                    created: guarantor.created,
                    event: `Guarantor`,
                    description: <span>{_.compact(parts).join(', ')}</span>,
                    Component: (
                        <GuarantorDetail
                            claimId={claimId}
                            guarantor={guarantor}
                            creditCards={creditCards}
                            paymentPlans={paymentPlans}
                        />
                    ),
                    selectable: true,
                });
            }
            return result;
        };

        const chargeEvents = (): IBillingTimelineEvent[] => {
            return _.compact(
                _.map(checkoutCharges, charge => {
                    if (charge.status === CheckoutChargeStatusType.REFUNDED) {
                        return;
                    }
                    return {
                        id: charge.id,
                        created: charge.chargeDate,
                        event: `Charge`,
                        description: (
                            <span>
                                {charge.status === CheckoutChargeStatusType.SUCCEEDED
                                    ? `${renderAmount(charge.amount)} charged`
                                    : charge.status}
                            </span>
                        ),
                        Component: <CheckoutChargeDetail checkoutCharge={charge} creditCards={creditCards} />,
                        selectable: true,
                    };
                }),
            );
        };

        const refundEvents = (): IBillingTimelineEvent[] => {
            const refunds = _.compact(_.flatMap(checkoutCharges, 'refunds'));
            return _.map(refunds, refund => {
                return {
                    id: refund.id,
                    created: refund.refundDate,
                    event: `Refund`,
                    description: <span>{renderAmount(refund.refundedAmount)} refunded</span>,
                    Component: <RefundDetail refund={refund} />,
                    selectable: true,
                };
            });
        };

        const invoiceEvents = (): IBillingTimelineEvent[] => {
            const events: IBillingTimelineEvent[] = _.compact(
                _.map(invoices, invoice => {
                    if (invoice.invoiceType === InvoiceType.PAYER) {
                        return;
                    }
                    const paymentPlansForInvoice = _.filter(paymentPlans, plan => plan.invoiceId === invoice.id);
                    
                    return {
                        id: invoice.id,
                        created: invoice.invoiceDate,
                        event: `${renderEnum(invoice.invoiceType)} Invoice`,
                        description: <span>{renderAmount(invoice.paidAmount)} Paid</span>,
                        Component: (
                            <InvoiceDetail
                                invoice={invoice}
                                appType="provider"
                                guarantor={guarantor}
                                payments={payments || []}
                                paymentPlans={paymentPlansForInvoice}
                            />
                        ),
                        selectable: true,
                    };
                }),
            );
            return events;
        };

        // Only show events for transactions that are un-connected to an invoice
        const transactionEvents = (): IBillingTimelineEvent[] => {
            const invoiceIds = _.map(invoices, 'id');
            const unmatchedTransactions = _.reject(transactions, transaction =>
                _.includes(invoiceIds, transaction.invoiceId),
            );
            const events: IBillingTimelineEvent[] = _.map(unmatchedTransactions, transaction => {
                return {
                    id: transaction.id,
                    created: transaction.created,
                    event: 'Transaction',
                    description: (
                        <span>
                            {renderAmount(transaction.amount)} billed by {transaction.billedByDescriptor}
                        </span>
                    ),
                    Component: <TransactionDetail transaction={transaction} />,
                    selectable: true,
                };
            });
            return events;
        };
        let allEvents = _.concat(
            [],
            guarantorEvent(),
            chargeEvents(),
            refundEvents(),
            invoiceEvents(),
            transactionEvents(),
        );
        allEvents = _.reverse(_.sortBy(allEvents, 'created'));
        return getGroupedEvents(allEvents, expandedIds);
    }, [checkoutCharges, claimId, creditCards, guarantor, invoices, payments, paymentPlans, transactions, expandedIds]);

    useEffect(() => {
        if (!loading) {
            const events = compileEvents();
            if (!_.isEmpty(events) && !_.isEqual(events, presenter.data)) {
                presenter.onDataUpdate(events);
                presenter.onDataTotalChange(events.length);
            }
        }
    }, [loading, presenter, compileEvents]);

    const onRowClick = (row: Row<IBillingTimelineEvent>): void => {
        if (row.original.Component) {
            row.toggleRowExpanded();
        }
    };

    const renderCaption = (): React.ReactNode | undefined => {
        if (!loading && guarantor && guarantor.dashboardUrl) {
            return <S.StyledLink href={guarantor.dashboardUrl}>Payment Dashboard</S.StyledLink>;
        }
    };

    const renderExpanded = (row: Row<IBillingTimelineEvent>): React.ReactElement => {
        const isLast = row.index === _.size(presenter.data) - 1;
        if (row.isExpanded) {
            return (
                <S.Expand>
                    {isLast && <S.ExpandSegment />}
                    {!isLast && (
                        <S.ExpandSegment>
                            <TimelineSegment />
                        </S.ExpandSegment>
                    )}
                    <DialogProvider>
                        <S.ExpandDetail>
                            {typeof row.original.Component === 'function' ? row.original.Component() : row.original.Component}
                        </S.ExpandDetail>
                    </DialogProvider>
                    <S.ExpandSegment />
                </S.Expand>
            );
        }
        return <></>;
    };

    const renderRowWrapper = (row: Row<IBillingTimelineEvent>, renderRow: React.ReactElement) => {
        const groupedItems = row.original.groupedItems || [];
        const handleExpandEvents = () => {
            if (row.original.id) {
                setExpandedIds(_.uniq([...expandedIds, row.original.id]));
            }
        };

        return (
            <div>
                {renderRow}
                {!_.isEmpty(groupedItems) && (
                    <S.RowGroupWrapper onClick={handleExpandEvents}>
                        There are {groupedItems.length} more similar events. Click to show.
                    </S.RowGroupWrapper>
                )}
            </div>
        );
    };

    return (
        <S.StyledTitleCard
            size="large"
            info="Shows guarantor information and a timeline of billing events"
            caption={renderCaption()}
            title="Billing Information">
            {!!Header && <S.StyledHeader>{Header}</S.StyledHeader>}
            <S.StyledDataTable
                presenter={presenter}
                isLoading={loading}
                onRowClick={onRowClick}
                renderRowSubComponent={renderExpanded}
                renderRowWrapper={renderRowWrapper}
                disableSorting={true}
                hideFiltering={true}
                hideNavigation={true}
            />
        </S.StyledTitleCard>
    );
};

function getGroupedEvents(items: IBillingTimelineEvent[], expandedIds: string[]): IBillingTimelineEvent[] {
    const res: IBillingTimelineEvent[] = [];
    let temp: IBillingTimelineEvent[] = [];

    for (const item of items) {
        const lastItem = _.last(temp);

        // when temp array is empty
        if (!lastItem) {
            temp.push(item);
            continue;
        }

        // build temp array, group the similar events
        if (item.event === lastItem.event) {
            temp.push(item);
        } else {
            addTemp();
            temp = [item];
        }
    }

    function addTemp() {
        const firstItem = _.first(temp);
        if (!firstItem?.id || expandedIds.includes(firstItem.id)) {
            res.push(...temp);
            return;
        }

        if (temp.length > 2) {
            firstItem.groupedItems = [...temp.slice(1)];
            res.push(firstItem);
        } else {
            res.push(...temp);
        }
    }

    // add the rest
    addTemp();

    return res;
}
